import React from 'react';
import { MenuItem } from '@mui/material';
import styled from 'styled-components';

import { CenteredCircularProgress } from 'components/Progress';
import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';
import API from 'utils/api';
import { APIRequest, SearchRequest, UuidableName } from 'utils/api/types';

const StyledOption = styled(MenuItem)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 143%;
  letter-spacing: 0.17px;
  width: 100%;
`;

const loadingOption: UuidableName = { uuid: 'loading', name: 'Loading...' };

const searchAuditForms = (params?: SearchRequest): APIRequest<UuidableName[]> =>
  API.entities.searchForms('audits', { ...params, ordering: 'name' });

const searchWorkflowsTemplates = (params?: SearchRequest): APIRequest<UuidableName[]> =>
  API.workflows.searchTemplates(params);

interface TemplateSearchFieldProps {
  value: UuidableName | null;
  onChange: (template: UuidableName | null) => void;
  // Must match what the submit payload keys off, or the picker offers one source and saves another.
  usesSubmissionForm: boolean;
  error?: boolean;
  helperText?: string;
}

const TemplateSearchField: React.FunctionComponent<TemplateSearchFieldProps> = ({
  value,
  onChange,
  usesSubmissionForm,
  error,
  helperText,
}) => {
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(
    usesSubmissionForm ? searchAuditForms : searchWorkflowsTemplates
  );

  const [selectedTemplate, setSelectedTemplate] = React.useState<UuidableName | null>(value);

  React.useEffect(() => {
    setSelectedTemplate(value);
  }, [value]);

  const handleChange = (_: React.SyntheticEvent, newValue: UuidableName | null): void => {
    const templateValue = newValue ? { uuid: newValue.uuid, name: newValue.name } : null;
    setSelectedTemplate(newValue);
    onChange(templateValue);
  };

  return (
    <InfiniteAutocomplete<UuidableName>
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      id="template-search-field"
      data-testid="template-search-field"
      options={results}
      loading={loading}
      value={selectedTemplate}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, val) => val?.uuid !== undefined && val?.uuid === option?.uuid}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder="Select audit template"
          error={error}
          onChange={(e) => {
            searchQuery(e.target.value);
          }}
          helperText={error ? helperText : undefined}
        />
      )}
      onChange={handleChange}
      renderOption={(props, option) =>
        option.uuid === loadingOption.uuid ? (
          <CenteredCircularProgress />
        ) : (
          <StyledOption {...props}>{option.name}</StyledOption>
        )
      }
      getOptionDisabled={(option) => option.uuid === loadingOption.uuid || option.uuid === selectedTemplate?.uuid}
      fullWidth
      blurOnSelect
    />
  );
};

export default TemplateSearchField;
